import { Plus, Trash2 } from "lucide-react";
import { emptySet, strengthSetDefaults } from "@/lib/workoutTaxonomy";

const inputCls =
    "w-full bg-black border border-zinc-800 px-2.5 py-2 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red text-center";

export default function SetsTable({ sets, onChange, testIdBase, validationError = null, category = null, exerciseName = null }) {
    const update = (i, patch) => {
        onChange(sets.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
    };
    const updateRound = (i, raw) => {
        // Round number is parsed as int; blank or invalid → keep previous.
        const n = parseInt(String(raw).replace(/\D/g, ""), 10);
        if (!Number.isFinite(n) || n < 1) return;
        update(i, { set_num: n });
    };
    const addSet = () => {
        // Sparse-set support: new row continues the round sequence from the
        // current MAX round number, so coaches who skipped a round earlier
        // get a sensible default that they can override.
        const maxRound = sets.reduce((m, s) => Math.max(m, Number(s.set_num) || 0), 0);
        const nextNum = maxRound + 1;
        const defaults = category === "strength" ? strengthSetDefaults(exerciseName) : null;
        const fresh = defaults
            ? { set_num: nextNum, ...defaults }
            : emptySet(nextNum);
        onChange([...sets, fresh]);
    };
    const addPyramid = () => {
        const pct = [60, 70, 80, 90];
        const rows = pct.map((p, i) => ({ set_num: i + 1, percent_max: String(p), reps: i === 3 ? "AMRAP" : "5", rest_mins: "2" }));
        onChange(rows);
    };
    const applyToAll = () => {
        if (!sets.length) return;
        const first = sets[0];
        // Preserve each row's existing set_num (round membership) — only
        // copy the prescription columns. This keeps sparse rounds intact.
        onChange(sets.map((s) => ({ ...first, set_num: s.set_num })));
    };
    const amrapLast = () => {
        if (!sets.length) return;
        onChange(sets.map((s, i) => i === sets.length - 1 ? { ...s, reps: "AMRAP" } : s));
    };
    const removeSet = (i) => {
        // Do NOT renumber set_num on removal — round membership is
        // semantically meaningful now (Sprint runs in rounds 1, 2, 3
        // while Throw runs in only 2 and 3). Renumbering would silently
        // shift Throw into rounds 1 and 2.
        const next = sets.filter((_, idx) => idx !== i);
        onChange(next.length ? next : [emptySet(1)]);
    };

    return (
        <div data-testid={`${testIdBase}-sets`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Sets</div>
                <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={addPyramid} className="text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-200 hover:text-brand-red hover:border-brand-red px-2.5 py-1.5">Pyramid</button>
                    <button type="button" onClick={applyToAll} className="text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-200 hover:text-brand-red hover:border-brand-red px-2.5 py-1.5">Apply 1st to all</button>
                    <button type="button" onClick={amrapLast} className="text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-200 hover:text-brand-red hover:border-brand-red px-2.5 py-1.5">AMRAP last</button>
                </div>
            </div>
            <div className="border border-zinc-900">
                <div className="grid grid-cols-12 gap-0 bg-black border-b border-zinc-900 text-[10px] tracking-[0.2em] uppercase text-zinc-300">
                    <div className="col-span-1 px-2 py-2 text-center" title="Round / set number — coaches can skip rounds to make this exercise sparse across a superset">Round</div>
                    <div className="col-span-3 px-2 py-2 text-center">% Max</div>
                    <div className="col-span-3 px-2 py-2 text-center">Reps</div>
                    <div className="col-span-3 px-2 py-2 text-center">Rest (min)</div>
                    <div className="col-span-2 px-2 py-2 text-right" />
                </div>
                {sets.map((s, i) => {
                    const rowHasErr = validationError && validationError.setIdx === i;
                    const badField = rowHasErr ? validationError.field : null;
                    const cellCls = (key) => `${inputCls} ${badField === key ? "border-brand-red ring-1 ring-brand-red/40" : ""}`;
                    return (
                        <div key={i} data-testid={`${testIdBase}-set-${i}`} className={`grid grid-cols-12 items-center bg-zinc-950 border-b border-zinc-900 last:border-b-0 ${rowHasErr ? "bg-red-950/15" : ""}`}>
                            <div className="col-span-1 px-1 py-1.5">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    data-testid={`${testIdBase}-set-${i}-round`}
                                    value={s.set_num ?? ""}
                                    onChange={(e) => updateRound(i, e.target.value)}
                                    title="Round number — change to skip a round (sparse-set membership)"
                                    className={`${inputCls} font-display text-xl text-brand-red`}
                                />
                            </div>
                            <div className="col-span-3 px-1.5 py-1.5">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="70"
                                    data-testid={`${testIdBase}-set-${i}-percent`}
                                    value={s.percent_max}
                                    onChange={(e) => update(i, { percent_max: e.target.value })}
                                    className={cellCls("percent_max")}
                                />
                            </div>
                            <div className="col-span-3 px-1.5 py-1.5">
                                <input
                                    type="text"
                                    placeholder="5"
                                    data-testid={`${testIdBase}-set-${i}-reps`}
                                    value={s.reps}
                                    onChange={(e) => update(i, { reps: e.target.value })}
                                    className={cellCls("reps")}
                                />
                            </div>
                            <div className="col-span-3 px-1.5 py-1.5">
                                <input
                                    type="text"
                                    placeholder="2"
                                    data-testid={`${testIdBase}-set-${i}-rest`}
                                    value={s.rest_mins}
                                    onChange={(e) => update(i, { rest_mins: e.target.value })}
                                    className={cellCls("rest_mins")}
                                />
                            </div>
                            <div className="col-span-2 px-2 py-1.5 text-right">
                                <button type="button" onClick={() => removeSet(i)} className="text-zinc-300 hover:text-brand-red"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                        </div>
                    );
                })}
            </div>
            <button type="button" onClick={addSet} data-testid={`${testIdBase}-add-set`} className="mt-2 text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-300 hover:text-brand-red hover:border-brand-red px-3 py-2 inline-flex items-center gap-1.5">
                <Plus className="w-3 h-3" /> Add set
            </button>
        </div>
    );
}
